import { ExternalLink, Plus, X } from 'lucide-react'
import { openUnsubPreview } from '@/pages/audiences/unsub-preview-html'

const DEFAULT_SUGGESTIONS = ['Too many emails', 'Content not relevant', "I didn't sign up for this"]

const MAX_REASONS = 10

interface UnsubFeedbackSectionProps {
  unsubMessage: string
  onUnsubMessageChange: (value: string) => void
  reasons: string[]
  onReasonsChange: (reasons: string[]) => void
  includeOther: boolean
  onIncludeOtherChange: (value: boolean) => void
  audienceName?: string
}

export function UnsubFeedbackSection({
  unsubMessage,
  onUnsubMessageChange,
  reasons,
  onReasonsChange,
  includeOther,
  onIncludeOtherChange,
  audienceName,
}: UnsubFeedbackSectionProps): React.JSX.Element {
  const hasNoReasons = reasons.filter((r) => r.trim()).length === 0 && !includeOther

  function handleAddReason(): void {
    if (reasons.length >= MAX_REASONS) return
    onReasonsChange([...reasons, ''])
  }

  function handleRemoveReason(index: number): void {
    onReasonsChange(reasons.filter((_, i) => i !== index))
  }

  function handleReasonChange(index: number, value: string): void {
    const updated = [...reasons]
    updated[index] = value
    onReasonsChange(updated)
  }

  function handleAddDefaults(): void {
    const existing = new Set(reasons.map((r) => r.toLowerCase().trim()))
    const toAdd = DEFAULT_SUGGESTIONS.filter((s) => !existing.has(s.toLowerCase()))
    const combined = [...reasons, ...toAdd].slice(0, MAX_REASONS)
    onReasonsChange(combined)
  }

  const placeholder = `You won't receive any more emails from ${audienceName ?? 'this list'}.`

  return (
    <div className="space-y-4 rounded-lg border border-surface-3 bg-surface-1/50 p-4">
      <div>
        <h3 className="text-sm font-medium text-text-primary">Unsubscribe Feedback</h3>
        <p className="mt-0.5 text-xs text-text-tertiary">
          Optional. If configured, unsubscribers see a feedback form.
        </p>
      </div>

      {/* Goodbye message */}
      <div>
        <label htmlFor="unsub-message" className="block text-sm font-medium text-text-primary">
          Goodbye message
        </label>
        <input
          id="unsub-message"
          type="text"
          value={unsubMessage}
          onChange={(e) => onUnsubMessageChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
        />
      </div>

      {/* Feedback reasons */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="block text-sm font-medium text-text-primary">
            Feedback reasons ({reasons.length}/{MAX_REASONS})
          </span>
          {reasons.length === 0 && (
            <button
              type="button"
              onClick={handleAddDefaults}
              className="text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              Add defaults
            </button>
          )}
        </div>

        <div className="space-y-2">
          {reasons.map((reason, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: mutable list items edited by index with no stable identifier
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={reason}
                onChange={(e) => handleReasonChange(index, e.target.value)}
                placeholder="Enter a reason..."
                maxLength={200}
                className="flex-1 rounded-lg border border-surface-3 bg-surface-0 px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => handleRemoveReason(index)}
                className="rounded p-1 text-text-tertiary hover:bg-surface-2 hover:text-red-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {reasons.length < MAX_REASONS && (
          <button
            type="button"
            onClick={handleAddReason}
            className="mt-2 flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Add reason
          </button>
        )}
      </div>

      {/* Include "Other" toggle */}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={includeOther}
          onChange={(e) => onIncludeOtherChange(e.target.checked)}
          className="h-4 w-4 rounded border-surface-3 text-brand-500 focus:ring-brand-500"
        />
        <span className="text-sm text-text-secondary">
          Include "Other" with freeform text field
        </span>
      </label>

      {/* Preview button */}
      <button
        type="button"
        disabled={hasNoReasons}
        onClick={() =>
          openUnsubPreview({
            audienceName: audienceName ?? 'this list',
            unsubMessage,
            reasons,
            includeOther,
          })
        }
        className="flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:text-text-tertiary disabled:cursor-not-allowed"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Preview Unsubscribe Page
      </button>
    </div>
  )
}
